import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import { Package, ArrowLeft, CheckCircle, MapPin } from 'lucide-react';
import { registrationApi } from '@/services/api/registrationApi';
import { Button, Input } from '@/components/ui';
import YandexMap from '@/components/ui/YandexMap';
import WorkingHoursEditor from '@/components/ui/WorkingHoursEditor';
import type { WorkingHoursValue } from '@/components/ui/WorkingHoursEditor';
import { extractErrorMessage } from '@/utils/extractErrorMessage';

const branchSchema = z.object({
  applicant_name: z.string().min(1, 'Введите ваше имя'),
  applicant_email: z.string().min(1, 'Введите email').email('Некорректный email'),
  applicant_phone: z.string().optional(),
  password: z.string().min(8, 'Минимум 8 символов'),
  confirm_password: z.string().min(1, 'Подтвердите пароль'),
  branch_name: z.string().min(1, 'Введите название ОЦ'),
  branch_address: z.string().min(1, 'Введите адрес'),
  branch_phone: z.string().optional(),
  latitude: z.number().optional(),
  longitude: z.number().optional(),
  working_hours: z.record(
    z.string(),
    z.union([
      z.object({ open: z.string(), close: z.string() }),
      z.null(),
    ]),
  ).optional(),
}).refine((data) => data.password === data.confirm_password, {
  message: 'Пароли не совпадают',
  path: ['confirm_password'],
});

type BranchForm = z.infer<typeof branchSchema>;

const DEFAULT_WORKING_HOURS: WorkingHoursValue = {
  monday: { open: '09:00', close: '18:00' },
  tuesday: { open: '09:00', close: '18:00' },
  wednesday: { open: '09:00', close: '18:00' },
  thursday: { open: '09:00', close: '18:00' },
  friday: { open: '09:00', close: '18:00' },
  saturday: { open: '10:00', close: '16:00' },
  sunday: null,
};

const RegisterBranchPage: React.FC = () => {
  const [submitted, setSubmitted] = useState(false);
  const [selectedPoint, setSelectedPoint] = useState<{ lat: number; lng: number } | null>(null);

  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<BranchForm>({
    resolver: zodResolver(branchSchema),
    defaultValues: {
      working_hours: DEFAULT_WORKING_HOURS,
    },
  });

  const handleMapClick = (lat: number, lng: number) => {
    setSelectedPoint({ lat, lng });
    setValue('latitude', lat);
    setValue('longitude', lng);
  };

  const onSubmit = async (data: BranchForm) => {
    try {
      await registrationApi.registerBranch({
        applicant_name: data.applicant_name,
        applicant_email: data.applicant_email,
        applicant_phone: data.applicant_phone || undefined,
        password: data.password,
        branch_name: data.branch_name,
        branch_address: data.branch_address,
        branch_phone: data.branch_phone || undefined,
        latitude: data.latitude,
        longitude: data.longitude,
        working_hours: data.working_hours as WorkingHoursValue | undefined,
      });
      setSubmitted(true);
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Ошибка отправки заявки. Попробуйте позже.'));
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="w-full max-w-md text-center">
          <div className="bg-white rounded-2xl shadow-soft border border-gray-100 p-8">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-5">
              <CheckCircle className="w-8 h-8 text-emerald-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Заявка отправлена!</h2>
            <p className="text-sm text-gray-500 leading-relaxed">
              Ваша заявка отправлена на модерацию. Мы рассмотрим её в ближайшее время и отправим уведомление на указанный email.
            </p>
            <Link
              to="/login"
              className="inline-flex items-center gap-1.5 mt-6 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Вернуться ко входу
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-8">
      <div className="w-full max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-primary-600 flex items-center justify-center mx-auto mb-4 shadow-card">
            <Package className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Регистрация ОЦ</h1>
          <p className="text-sm text-gray-500 mt-1">Подайте заявку на регистрацию пункта выдачи</p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="bg-white rounded-2xl shadow-soft border border-gray-100 p-6 sm:p-8">
            <h3 className="text-base font-semibold text-gray-900 mb-4">Ваши данные</h3>
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input
                  label="Ваше имя"
                  placeholder="Иванов Иван Иванович"
                  error={errors.applicant_name?.message}
                  required
                  {...register('applicant_name')}
                />
                <Input
                  label="Email"
                  type="email"
                  placeholder="owner@company.ru"
                  error={errors.applicant_email?.message}
                  required
                  {...register('applicant_email')}
                />
              </div>
              <Input
                label="Телефон"
                type="tel"
                placeholder="+7 (999) 123-45-67"
                error={errors.applicant_phone?.message}
                {...register('applicant_phone')}
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input
                  label="Пароль"
                  type="password"
                  placeholder="••••••••"
                  error={errors.password?.message}
                  required
                  {...register('password')}
                />
                <Input
                  label="Подтверждение пароля"
                  type="password"
                  placeholder="••••••••"
                  error={errors.confirm_password?.message}
                  required
                  {...register('confirm_password')}
                />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-soft border border-gray-100 p-6 sm:p-8">
            <h3 className="text-base font-semibold text-gray-900 mb-4">Данные пункта выдачи</h3>
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input
                  label="Название ОЦ"
                  placeholder="ОЦ «Центральный»"
                  error={errors.branch_name?.message}
                  required
                  {...register('branch_name')}
                />
                <Input
                  label="Телефон ОЦ"
                  type="tel"
                  placeholder="+7 (495) 000-00-00"
                  error={errors.branch_phone?.message}
                  {...register('branch_phone')}
                />
              </div>
              <Input
                label="Адрес"
                placeholder="г. Москва, ул. Ленина, д. 1"
                error={errors.branch_address?.message}
                required
                {...register('branch_address')}
              />

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Расположение на карте
                </label>
                <p className="text-xs text-gray-400 mb-2">
                  Нажмите на карту, чтобы указать местоположение
                </p>
                <YandexMap
                  onMapClick={handleMapClick}
                  selectedPoint={selectedPoint}
                  height="280px"
                />
                {selectedPoint && (
                  <div className="mt-2 flex items-center gap-1.5 text-xs text-gray-500">
                    <MapPin className="w-3.5 h-3.5" />
                    <span>
                      {selectedPoint.lat.toFixed(6)}, {selectedPoint.lng.toFixed(6)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-soft border border-gray-100 p-6 sm:p-8">
            <Controller
              name="working_hours"
              control={control}
              render={({ field }) => (
                <WorkingHoursEditor
                  value={(field.value ?? DEFAULT_WORKING_HOURS) as WorkingHoursValue}
                  onChange={field.onChange}
                />
              )}
            />
          </div>

          <Button
            type="submit"
            loading={isSubmitting}
            className="w-full"
            size="lg"
          >
            Отправить заявку
          </Button>
        </form>

        <div className="text-center mt-6 mb-8">
          <Link
            to="/login"
            className="inline-flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Вернуться ко входу
          </Link>
        </div>
      </div>
    </div>
  );
};

export default RegisterBranchPage;
